import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Collapse,
  IconButton,
  Tooltip,
  Button,
  Avatar,
  Chip,
  alpha,
  Paper,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useBranch } from '../BranchContext';
import { useAuth } from '../context/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import InventoryIcon from '@mui/icons-material/Inventory';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WalletIcon from '@mui/icons-material/Wallet';
import PaymentIcon from '@mui/icons-material/Payment';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ViewListIcon from '@mui/icons-material/ViewList';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PhoneIcon from '@mui/icons-material/Phone';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MenuIcon from '@mui/icons-material/Menu';
import LockIcon from '@mui/icons-material/Lock';
import BackupIcon from '@mui/icons-material/Backup';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
// ✅ NEW: Design System Icons
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import BrushIcon from '@mui/icons-material/Brush';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryIcon from '@mui/icons-material/History';

const drawerWidth = 270;

export default function Layout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const { selectedBranch } = useBranch();
  const { logout, user } = useAuth();

  const hasPermission = user && user.Permission === true;
  const isRTL = i18n.language === 'ar';
  const adminName = user?.Name || 'Admin';
  const adminNumber = user?.Number || '';

  const menuItems = useMemo(() => [
    // Dashboard only for admins with permission
    ...(hasPermission ? [{ 
      id: 'dashboard', 
      text: t('Dashboard'), 
      icon: <DashboardIcon />, 
      path: '/admin',
      color: '#1976d2'
    }] : []),
    {
      id: 'salesMenu',
      text: t('Sales'),
      icon: <AssessmentIcon />,
      color: '#2e7d32',
      children: [
        hasPermission && { id: 'allSales', text: t('AllBranchesSales'), path: '/admin/sales' },
        { id: 'branchSales', text: t('ThisBranchSales'), path: '/admin/sales/branch' }
      ].filter(Boolean)
    },
    {
      id: 'orders',
      text: t('Orders'),
      icon: <ShoppingCartIcon />,
      color: '#ed6c02',
      children: [
        { id: 'liveOrders', text: t('ViewOrders'), path: '/admin/orders' },
        { id: 'orderHistory', text: t('OrderHistory'), path: '/admin/orders/history' }
      ]
    },
    {
      id: 'products',
      text: t('Products'),
      icon: <InventoryIcon />,
      color: '#9c27b0',
      children: [
        { id: 'allProducts', text: t('Products'), path: '/admin/products' },
        { id: 'addProduct', text: t('AddProduct'), path: '/admin/products/add' },
        { id: 'addCustomProduct', text: t('AddCustomProduct', 'Add Custom Product'), path: '/admin/products/add-custom' },
        { id: 'groupedProducts', text: t('GroupedProducts'), path: '/admin/products/grouped' },
        { id: 'offers', text: t('OffersDeals'), path: '/admin/offers' },
        {
          id: 'rentals',
          text: t('RentalManagement') || 'Rental Management',
          icon: <HomeRepairServiceIcon />,
          path: '/admin/rentals',
          color: '#00838f'
        },
      ]
    },
    {
      id: 'categories',
      text: t('Categories'),
      icon: <CategoryIcon />,
      color: '#0288d1',
      children: [
        { id: 'manageCategories', text: t('Categories'), path: '/admin/categories' },
        { id: 'addCategory', text: t('AddCategory'), path: '/admin/categories/add' }
      ]
    },
    // Filters - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'filters',
      text: t('Filters'),
      icon: <FilterAltIcon />,
      path: '/admin/filters',
      color: '#00796b'
    }] : []),
    // Wallets - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'wallets',
      text: t('Wallets') || 'Payment Wallets',
      icon: <WalletIcon />,
      color: '#1565c0',
      children: [
        { id: 'manageWallets', text: t('ManageWallets') || 'Manage Wallets', path: '/admin/wallets' },
        { id: 'addWallet', text: t('AddWallet') || 'Add Wallet', path: '/admin/wallets/add' }
      ]
    }] : []),
    {
      id: 'transactions',
      text: t('Transactions') || 'Payment Verifications',
      icon: <PaymentIcon />,
      color: '#6a1b9a',
      children: [
        { id: 'pendingTransactions', text: t('PendingVerifications') || 'Pending Verifications', path: '/admin/transactions' },
        { id: 'transactionHistory', text: t('TransactionHistory') || 'Transaction History', path: '/admin/transactions/history' }
      ]
    },
    // Branches - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'branches',
      text: t('Branches'),
      icon: <StoreIcon />,
      color: '#5e35b1',
      children: [
        { id: 'manageBranches', text: t('Branches'), path: '/admin/branches' },
        { id: 'acceptingStores', text: t('AcceptingStores') || 'Accepting Stores', path: '/admin/accepting-stores' },
        { id: 'addBranch', text: t('AddBranch'), path: '/admin/branches/add' }
      ]
    }] : []),
    // Cities - Only for permissioned admins
    ...(hasPermission ? [{ 
      id: 'cities', 
      text: t('ManageCities'), 
      icon: <LocationCityIcon />, 
      path: '/admin/cities',
      color: '#c62828'
    }] : []),
    {
      id: 'drivers',
      text: t('Drivers'),
      icon: <DriveEtaIcon />,
      color: '#00838f',
      children: [
        { id: 'manageDrivers', text: t('ManageDrivers'), path: '/admin/drivers' },
        { id: 'registerDriver', text: t('RegisterDriver'), path: '/admin/drivers/register' }
      ]
    },
    // Restaurant - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'restaurant',
      text: t('Restaurant'),
      icon: <RestaurantIcon />,
      color: '#d84315',
      children: [
        { id: 'infoSettings', text: t('InfoSettings'), path: '/admin/restaurant' },
        { id: 'sliderImages', text: t('SliderImages'), path: '/admin/restaurant/slider' }
      ]
    }] : []),
    // Users - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'users',
      text: t('Users'),
      icon: <PeopleIcon />,
      color: '#6a1b9a',
      children: [
        { id: 'manageUsers', text: t('Users'), path: '/admin/users' },
        { id: 'registerAdmin', text: t('RegisterAdmin') || 'Register Admin', path: '/admin/admins/register' }
      ]
    }] : []),
    // Coupons & Vouchers - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'coupons',
      text: t('CouponsVouchers') || 'Coupons & Vouchers',
      icon: <LocalOfferIcon />,
      color: '#e65100',
      children: [
        { id: 'manageCoupons', text: t('ManageCoupons') || 'Manage Coupons', path: '/admin/coupons' },
        { id: 'addCoupon', text: t('AddCoupon') || 'Add Coupon', path: '/admin/coupons/add' }
      ]
    }] : []),
    // QR Codes - Only for permissioned admins
    ...(hasPermission ? [{
      id: 'qrcodes',
      text: t('QRCodes') || 'QR Codes',
      icon: <QrCode2Icon />,
      color: '#00897b',
      children: [
        { id: 'manageQRCodes', text: t('ManageQRCodes') || 'Manage QR Codes', path: '/admin/qrcodes' },
        { id: 'generateQRCodes', text: t('GenerateQRCodes') || 'Generate QR Codes', path: '/admin/qrcodes/generate' }
      ]
    }] : []),

    // ✅ NEW: Design Services - Super Admin (with permission)
    ...(hasPermission ? [{
      id: 'designAdmin',
      text: t('DesignServices') || 'Design Services',
      icon: <DesignServicesIcon />,
      color: '#ad1457',
      children: [
        { id: 'designPlans', text: t('DesignPlans') || 'Design Plans', path: '/admin/design-plans' },
        { id: 'designRequests', text: t('DesignRequests') || 'Design Requests', path: '/admin/design-requests' },
        { id: 'designManagement', text: t('DesignManagement') || 'Design Management', path: '/admin/design-management' },
        { id: 'designTransactions', text: t('DesignTransactions') || 'Design Transactions', path: '/admin/design-transactions' }
      ]
    }] : []),

    // ✅ NEW: Design Services - Regular Admin (without permission)
    ...(!hasPermission ? [{
      id: 'designUser',
      text: t('DesignServices') || 'Design Services',
      icon: <BrushIcon />,
      color: '#ad1457',
      children: [
        { id: 'purchaseDesign', text: t('PurchaseDesignPlan') || 'Purchase Design Plan', path: '/admin/purchase-design' },
        { id: 'myDesignSubs', text: t('MyDesignSubscriptions') || 'My Subscriptions', path: '/admin/my-design-subscriptions' }
      ]
    }] : []),

    { 
      id: 'feedback', 
      text: t('Feedback'), 
      icon: <ViewListIcon />, 
      path: '/admin/feedback',
      color: '#f57c00'
    },
    // Notifications - Only for permissioned admins
    ...(hasPermission ? [{ 
      id: 'notifications', 
      text: t('Notifications'), 
      icon: <AddBoxIcon />, 
      path: '/admin/notifications',
      color: '#c2185b'
    }] : []),
    // Backup Management - Only for permissioned admins
    ...(hasPermission ? [{ 
      id: 'backups', 
      text: t('BackupManagement') || 'Backup Management', 
      icon: <BackupIcon />, 
      path: '/admin/backups',
      color: '#00695c'
    }] : [])
  ], [t, hasPermission]);

  useEffect(() => {
    const activeItem = menuItems.find(item =>
      item.children && item.children.some(child => child.path === location.pathname)
    );
    if (activeItem) {
      setOpenSubmenus(prev => ({ ...prev, [activeItem.id]: true }));
    }
  }, [location.pathname, menuItems]);

  const handleSubmenuClick = (id) => {
    setOpenSubmenus((prev) => {
      const isOpen = prev[id];
      return isOpen ? {} : { [id]: true };
    });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isTablet) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const drawerContent = (
    <>
      <Toolbar />
      
      {/* Admin Profile Card */}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, mt: '-30px', borderBottom: `1px solid ${alpha('#fff', 0.1)}` }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            bgcolor: alpha('#fff', 0.05),
            borderRadius: 2,
            mt: '-30px', 
            border: `1px solid ${alpha('#fff', 0.1)}`,
          }}
        >
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
              <Avatar 
                sx={{ 
                  bgcolor: 'primary.main', 
                  width: { xs: 38, sm: 42, md: 45 }, 
                  height: { xs: 38, sm: 42, md: 45 },
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  fontWeight: 700
                }}
              >
                {adminName.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#fff', 
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {adminName}
                </Typography>
                <Chip 
                  label={hasPermission ? "مدير المنصة" : "مدير المتجر"}
                  size="small"
                  sx={{ 
                    height: { xs: 18, sm: 20 },
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    bgcolor: hasPermission ? 'success.main' : 'info.main',
                    color: '#fff',
                    mt: 0.5
                  }}
                />
              </Box>
            </Stack>

            {adminNumber && (
              <Stack 
                direction="row" 
                spacing={1} 
                alignItems="center"
                sx={{
                  pt: { xs: 0.8, sm: 1 },
                  mt: { xs: 0.8, sm: 1 },
                  borderTop: `1px solid ${alpha('#fff', 0.1)}`
                }}
              >
                <PhoneIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: alpha('#fff', 0.6) }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: alpha('#fff', 0.8),
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }}
                >
                  {adminNumber}
                </Typography>
              </Stack>
            )}

            {/* Reset Password Button */}
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={() => handleNavigation('/admin/admins/reset-password')}
              startIcon={<LockIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              sx={{
                mt: 1,
                borderColor: alpha('#fff', 0.2),
                color: alpha('#fff', 0.8),
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                py: { xs: 0.5, sm: 0.7 },
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.light'
                }
              }}
            >
              {t('ResetPassword') || 'Reset Password'}
            </Button>
          </Stack>
        </Paper>
      </Box>

      {/* Menu List */}
      <Box sx={{ overflow: 'auto', py: { xs: 0.5, sm: 1 }, flex: 1 }}>
        <List sx={{ px: { xs: 0.5, sm: 1 } }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={item.path ? location.pathname === item.path : false}
                  onClick={() => item.children ? handleSubmenuClick(item.id) : handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    mx: { xs: 0.3, sm: 0.5 },
                    minHeight: { xs: 42, sm: 45, md: 48 },
                    px: { xs: 2, sm: 2.5 },
                    color: alpha('#fff', 0.7),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.08),
                      color: '#fff',
                      transform: 'translateX(4px)',
                      '& .MuiListItemIcon-root': {
                        color: item.color || 'primary.main',
                        transform: 'scale(1.1)'
                      }
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(item.color || theme.palette.primary.main, 0.15),
                      color: '#fff',
                      borderLeft: `3px solid ${item.color || theme.palette.primary.main}`,
                      '&:hover': {
                        bgcolor: alpha(item.color || theme.palette.primary.main, 0.2),
                      },
                      '& .MuiListItemIcon-root': {
                        color: item.color || 'primary.main'
                      }
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: { xs: 1.5, sm: 2 },
                      color: 'inherit',
                      transition: 'all 0.3s ease',
                      fontSize: { xs: 20, sm: 22, md: 24 }
                    }}
                  >
                    {React.cloneElement(item.icon, { 
                      sx: { fontSize: { xs: 20, sm: 22, md: 24 } } 
                    })}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                      fontWeight: 500
                    }}
                  />
                  {item.children && (
                    openSubmenus[item.id] ? 
                      <ExpandLess sx={{ fontSize: { xs: 18, sm: 20 } }} /> : 
                      <ExpandMore sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  )}
                </ListItemButton>
              </ListItem>
              
              {item.children && (
                <Collapse in={openSubmenus[item.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.id}
                        sx={{ 
                          pl: { xs: 4.5, sm: 5, md: 5.5 },
                          pr: { xs: 2, sm: 2.5 },
                          py: { xs: 0.8, sm: 1 },
                          mx: { xs: 0.3, sm: 0.5 },
                          mb: 0.3,
                          borderRadius: 2,
                          color: alpha('#fff', 0.6),
                          minHeight: { xs: 36, sm: 38, md: 40 },
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha('#fff', 0.05),
                            color: '#fff',
                            paddingLeft: { xs: '2.5rem', sm: '2.8rem', md: '3rem' }
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha('#fff', 0.1),
                            color: '#fff',
                            borderLeft: `2px solid ${alpha(item.color || theme.palette.primary.main, 0.5)}`,
                            '&:hover': {
                              bgcolor: alpha('#fff', 0.12)
                            }
                          }
                        }}
                        selected={location.pathname === child.path}
                        onClick={() => handleNavigation(child.path)}
                      >
                        <ListItemText 
                          primary={child.text}
                          primaryTypographyProps={{
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Current Branch Display */}
      {selectedBranch && (
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          borderTop: `1px solid ${alpha('#fff', 0.1)}`,
          bgcolor: alpha('#fff', 0.03)
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: alpha('#fff', 0.5), 
              display: 'block', 
              mb: 0.5,
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
            }}
          >
            {t('CurrentBranch') || 'Current Branch'}
          </Typography>
          <Chip
            icon={<StoreIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
            label={selectedBranch.name}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.2),
              color: '#fff',
              fontWeight: 500,
              maxWidth: '100%',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 24, sm: 26 },
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          />
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', position: 'relative', minHeight: 'calc(100vh - 100px)', zIndex: 1 }}>
      <CssBaseline />
      
      {/* AppBar */}
      <AppBar 
        position="absolute" 
        sx={{ 
          zIndex: 1100, 
          mt: '-120px', 
          pb: '15px',
          pt: '30px',
          bgcolor: '#fff',
          color: 'text.primary',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderBottom: `1px solid ${alpha('#000', 0.08)}`
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          minHeight: { xs: 56, sm: 64, md: 70 },
          px: { xs: 2, sm: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: '20px', gap: { xs: 1, sm: 2 } }}>
            {/* Mobile Menu Button */}
            {isTablet && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Avatar 
              sx={{ 
                bgcolor: 'primary.main',
                width: { xs: 36, sm: 40, md: 42 },
                height: { xs: 36, sm: 40, md: 42 },
                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)'
              }}
            >
              <StoreIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
            </Avatar>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary', 
                  lineHeight: 1.2,
                  fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                }}
              >
                {t('StoresYeAdminPortal') || 'StoresYe Admin Portal'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', { 
                  weekday: 'long', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
            <Button
              color="inherit"
              onClick={toggleLanguage}
              startIcon={<LanguageIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: 'text.primary',
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              {i18n.language === 'en' ? 'العربية' : 'English'}
            </Button>
            
            <Tooltip title={t('Logout')}>
              <IconButton 
                color="inherit" 
                onClick={handleLogout}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  color: 'error.main',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.1)
                  }
                }}
              >
                <LogoutIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Drawer - Desktop */}
      {!isTablet && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            zIndex: 1000,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              zIndex: 1000,
              background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
              border: 'none'
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Drawer - Tablet/Mobile */}
      {isTablet && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
              mt: '90px', 
              pb: '15px',
              background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
              border: 'none'
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          minWidth: 0, 
          overflowX: 'auto',
          bgcolor: '#f5f7fa',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Box sx={{ 
          mt: '-80px', 
          animation: 'fadeIn 0.3s ease-in',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          }
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}